import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import { DateSerializer, Form, RadioGroup, SubmitButton, useForm } from "capitalrx-components-2.0/lib/forms";
import { Datebox } from "capitalrx-components-2.0/lib/forms/Datebox";
import { Stack, Shelf } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading } from "capitalrx-components-2.0/lib/typography";
import { Spinner, useAlert } from "capitalrx-components-2.0/lib/general";
import { Field as TextField } from "capitalrx-components-2.0/lib/forms/Textbox/Field";

import { financeGetRemittanceAPI } from "@/api";
import { ThemeSelectorContext } from "@/context";
import { getThemeButton, getThemeURL } from "@/utils";

import styles from "./PharmacyForm.module.scss";

export interface PharmacyForm {
  readonly check_amount?: number;
  readonly check_date?: string;
  readonly transaction_id?: string;
  readonly payment_date?: string;
  readonly search_type: "check" | "transaction_id";
}

export const PharmacyForm: FC = () => {
  const form = useForm<PharmacyForm>({
    defaultValues: {
      search_type: "check",
    },
  });
  const { control } = form;

  const searchSelection = useWatch({ control, name: "search_type" });
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const showAlert = useAlert();
  const { primary_color, primary_hover_color } = useContext(ThemeSelectorContext);

  useEffect(() => {
    if (searchSelection === "check") {
      form.setValue("transaction_id", undefined);
    }
    if (searchSelection === "transaction_id") {
      form.setValue("check_amount", undefined);
    }
  }, [searchSelection]);

  const handleSubmit = useCallback(
    async (data: PharmacyForm) => {
      try {
        setLoading(true);
        await financeGetRemittanceAPI({
          check_date: data.check_date,
          check_amount: data.check_amount,
          transaction_id: data.transaction_id,
        });

        navigate(getThemeURL("PHARMACY_SUBMIT"));

      } catch (error) {
        console.error(error);
        showAlert({ type: "error", style: "banner", message: "Failed to process information, please try again." });
      } finally {
        setLoading(false);
      }

    },
    [showAlert],
  );

  return (
    <div className={styles.PharmacyFormContainer}>
      {loading && (
        <Stack alignment="center" style={{ paddingTop: "200px" }}>
          <Spinner />
        </Stack>
      )}
      {!loading && (
        <Stack spacing="small">
          <Heading className={styles.PharmacyFormCenterText}>Pharmacy</Heading>
          <Body className={styles.PharmacyFormCenterText}>
            Enter details from your payment to receive remittance payment information files to the email on record with Capital Rx
          </Body>
          <Form form={form} onSubmit={handleSubmit}>
            <Stack>
              <RadioGroup.Field
                name="search_type"
                label="Search By"
                defaultValue={"check"}
                testId="test__search_by"
              >
                <RadioGroup.Option value="check">Check date and amount</RadioGroup.Option>
                <RadioGroup.Option value="transaction_id">Payment date and transaction ID</RadioGroup.Option>
              </RadioGroup.Field>
              {searchSelection === "check" && (
                <>
                  <Datebox.Field
                    required
                    label="Check Date"
                    name={"check_date"}
                    instructions={"mm/dd/yyyy"}
                    zone="local"
                    serializer={DateSerializer.Literal}
                    testId="test__check_date"
                  />
                  <TextField
                    required
                    label="Check Amount"
                    name={"check_amount"}
                    instructions={"No commas or special characters"}
                    placeholder="$"
                    testId="test__check_amount"
                  />
                </>
              )}
              {searchSelection === "transaction_id" && (
                <>
                  <Datebox.Field
                    required
                    label="Payment Date"
                    name={"check_date"}
                    instructions={"mm/dd/yyyy"}
                    zone="local"
                    serializer={DateSerializer.Literal}
                    testId="test__payment_date"
                  />
                  <TextField
                    required
                    label="Transaction ID"
                    name={"transaction_id"}
                    testId="test__transaction_id"
                  />
                </>
              )}
              <Shelf>
                <SubmitButton
                  appearance={getThemeButton("primary", primary_color, primary_hover_color)}
                  testId="test__pharmacy_submit"
                >
                  Submit
                </SubmitButton>
              </Shelf>
            </Stack>
          </Form>
        </Stack>
      )}
    </div>
  );
};

export default PharmacyForm;
